import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  handleSignIn,
  handleSignOut,
  handleConfirmNewPassword,
  handleResetPassword,
  handleConfirmResetPassword,
  getAuthenticatedUser,
  isLoggedIn,
  type AuthenticatedUser,
  type UserCredentials,
} from '@/services/auth';

export type AuthResult =
  | { kind: 'success' }
  | { kind: 'new-password-required' }
  | { kind: 'code-sent' }
  | { kind: 'error'; message: string };

interface AuthState {
  user: AuthenticatedUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  pendingResetEmail: string | null;

  signIn: (credentials: UserCredentials) => Promise<AuthResult>;
  confirmNewPassword: (newPassword: string) => Promise<AuthResult>;
  requestPasswordReset: (email: string) => Promise<AuthResult>;
  confirmPasswordReset: (email: string, code: string, newPassword: string) => Promise<AuthResult>;
  signOut: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
  clearError: () => void;
  setUser: (user: AuthenticatedUser | null) => void;
}

async function finishSignedIn(set: (partial: Partial<AuthState>) => void): Promise<AuthResult> {
  const user = await getAuthenticatedUser();
  set({ user, isAuthenticated: true, isLoading: false });
  return { kind: 'success' };
}

function failure(set: (partial: Partial<AuthState>) => void, error: unknown, fallback: string): AuthResult {
  const message = error instanceof Error ? error.message : fallback;
  set({ error: message, isLoading: false });
  return { kind: 'error', message };
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,
      pendingResetEmail: null,

      signIn: async (credentials) => {
        set({ isLoading: true, error: null });
        try {
          const result = await handleSignIn(credentials);
          if (result.isSignedIn) return finishSignedIn(set);
          if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
            set({ isLoading: false, isAuthenticated: false });
            return { kind: 'new-password-required' };
          }
          const message = `Sign-in requires unsupported step: ${result.nextStep?.signInStep ?? 'unknown'}`;
          set({ error: message, isLoading: false, isAuthenticated: false });
          return { kind: 'error', message };
        } catch (error) {
          set({ isAuthenticated: false });
          return failure(set, error, 'Sign in failed');
        }
      },

      confirmNewPassword: async (newPassword) => {
        set({ isLoading: true, error: null });
        try {
          const result = await handleConfirmNewPassword(newPassword);
          if (result.isSignedIn) return finishSignedIn(set);
          const message = `Unexpected step after new password: ${result.nextStep?.signInStep ?? 'unknown'}`;
          set({ error: message, isLoading: false });
          return { kind: 'error', message };
        } catch (error) {
          return failure(set, error, 'Failed to set new password');
        }
      },

      requestPasswordReset: async (email) => {
        set({ isLoading: true, error: null });
        try {
          await handleResetPassword(email);
          set({ isLoading: false, pendingResetEmail: email });
          return { kind: 'code-sent' };
        } catch (error) {
          return failure(set, error, 'Failed to send reset code');
        }
      },

      confirmPasswordReset: async (email, code, newPassword) => {
        set({ isLoading: true, error: null });
        try {
          await handleConfirmResetPassword(email, code, newPassword);
          set({ isLoading: false, pendingResetEmail: null });
          return { kind: 'success' };
        } catch (error) {
          return failure(set, error, 'Failed to reset password');
        }
      },

      signOut: async () => {
        set({ isLoading: true });
        try {
          await handleSignOut();
        } finally {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            pendingResetEmail: null,
          });
        }
      },

      checkAuth: async () => {
        set({ isLoading: true });
        try {
          const authenticated = await isLoggedIn();
          if (authenticated) {
            const user = await getAuthenticatedUser();
            set({ user, isAuthenticated: true, isLoading: false });
            return true;
          }
          set({ user: null, isAuthenticated: false, isLoading: false });
          return false;
        } catch {
          set({ user: null, isAuthenticated: false, isLoading: false });
          return false;
        }
      },

      clearError: () => set({ error: null }),

      setUser: (user) => set({ user, isAuthenticated: !!user }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ isAuthenticated: state.isAuthenticated }),
    }
  )
);
